import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { digitalOcean } from "@/integrations/digitalocean/client";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { createPageUrl } from "@/utils";
import { getDefaultCoverImage } from "@/utils/defaultCoverImage";

export default function ViewFeedback() {
  const location = useLocation();
  const navigate = useNavigate();
  const feedbackId = new URLSearchParams(location.search).get("id");
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    loadUser();
  }, []);

  const loadUser = async () => {
    try {
      if (!session) {
        navigate(createPageUrl("Auth"));
        return;
      }
      const { data: profile, error } = await digitalOcean
        .from("profiles")
        .select("*")
        .eq("id", session.user.id)
        .single();
      
      if (error) {
        console.error("Error loading profile:", error);
        navigate(createPageUrl("Auth"));
        return;
      }
      
      setCurrentUser(profile);
    } catch (error) {
      console.error("Error loading user:", error);
      navigate(createPageUrl("Auth"));
    }
  };

  const { data: feedback, isLoading } = useQuery({
    queryKey: ["feedback", feedbackId],
    queryFn: async () => {
      if (!feedbackId) return null;
      
      const { data, error } = await digitalOcean
        .from("feedback")
        .select(`
          *,
          track:tracks(title, artist_name, cover_image_url, audio_url)
        `)
        .eq("id", feedbackId)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!feedbackId && !!currentUser,
  });

  if (isLoading || !currentUser) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!feedback) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl text-foreground mb-4">Feedback Not Found</h1>
          <Button onClick={() => navigate(-1)} tooltip="Return to previous page">Go Back</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-8">
      <div className="max-w-4xl mx-auto">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6" tooltip="Go back to previous page">
          ← Back
        </Button>

        <div className="bg-card p-8 rounded-lg border border-border">
          <div className="flex items-start gap-6 mb-6">
            <img
              src={feedback.track?.cover_image_url || getDefaultCoverImage(feedback.track?.id)}
              alt={feedback.track?.title}
              className="w-32 h-32 rounded-lg object-cover"
            />
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-card-foreground mb-2">
                {feedback.track?.title}
              </h1>
              <p className="text-xl text-muted-foreground mb-4">
                by {feedback.track?.artist_name}
              </p>
              <Badge variant={
                feedback.feedback_type === 'praise' ? 'default' :
                feedback.feedback_type === 'scathe' ? 'destructive' :
                'secondary'
              } className="text-base px-4 py-1">
                {feedback.feedback_type}
              </Badge>
            </div>
          </div>

          <div className="border-t border-border pt-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                <span className="text-muted-foreground text-lg">?</span>
              </div>
              <div>
                <div className="font-semibold text-card-foreground">
                  Anonymous Reviewer
                </div>
                <div className="text-sm text-muted-foreground">
                  {new Date(feedback.created_at).toLocaleDateString()} at {new Date(feedback.created_at).toLocaleTimeString()}
                </div>
              </div>
            </div>

            {feedback.comment ? (
              <div className="bg-muted/50 p-6 rounded-lg">
                <p className="text-card-foreground text-lg leading-relaxed whitespace-pre-wrap">
                  {feedback.comment}
                </p>
              </div>
            ) : (
              <p className="text-muted-foreground italic">No written feedback provided.</p>
            )}
          </div>

          <div className="flex gap-4">
            <Button onClick={() => navigate(`/TrackDetails?id=${feedback.track_id}`)}>
              View Track Details
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
